import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import {Box, ButtonBase} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import React from "react";
import {signingControls} from "./signingColors";
import {controlRadius} from "./signingTheme";

interface Props {
    selected: boolean;
    highlighted?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    onClick?: () => void;
    label: string;
}

export const TIP_PILL_HEIGHT = {xs: 56, sm: 68};

const DURATION = '140ms';
const CHECK_SIZE = 20;
const CHECK_GAP = 4;
const SLIDE = (CHECK_SIZE + CHECK_GAP) / 2;

function TipPill({selected, highlighted, disabled, readOnly, onClick, label}: Props) {
    const theme = useTheme();
    const controls = signingControls(theme);

    const sx = {
        position: 'relative',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: TIP_PILL_HEIGHT,
        px: '12px',
        py: 1,
        borderRadius: `${controlRadius(theme)}px`,
        border: '1px solid',
        borderColor: selected ? controls.fill : highlighted ? controls.accent : controls.border,
        boxShadow: highlighted && !selected ? `inset 0 0 0 1px ${controls.accent}` : 'none',
        bgcolor: selected ? controls.fill : 'transparent',
        color: selected ? controls.onFill : 'text.primary',
        textAlign: 'center',
        fontVariantNumeric: 'tabular-nums',
        transition: `background-color ${DURATION} ease-out, border-color ${DURATION} ease-out, color ${DURATION} ease-out`,
        printColorAdjust: 'exact',
        WebkitPrintColorAdjust: 'exact',
        '& .TipPill-label': {
            position: 'relative',
            display: 'inline-block',
            transform: selected ? `translateX(${SLIDE}px)` : 'none',
            transition: `transform ${DURATION} ease-out`,
        },
        '& .TipPill-check': {
            position: 'absolute',
            right: `calc(100% + ${CHECK_GAP}px)`,
            top: '50%',
            fontSize: CHECK_SIZE,
            pointerEvents: 'none',
            transform: 'translateY(-50%) scale(1)',
            animation: `TipPill-checkIn ${DURATION} ease-out`,
        },
        '@keyframes TipPill-checkIn': {
            from: {opacity: 0, transform: 'translateY(-50%) scale(0.5)'},
            to: {opacity: 1, transform: 'translateY(-50%) scale(1)'},
        },
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            '& .TipPill-label': {transition: 'none'},
            '& .TipPill-check': {animation: 'none'},
        },
    } as const;

    const content = (
        <Box
            component={'span'}
            sx={{display: 'block', maxWidth: '100%', fontSize: {xs: 16, sm: 18}, fontWeight: 500, lineHeight: 1.25, overflowWrap: 'break-word', hyphens: 'auto'}}
        >
            <Box component={'span'} className={'TipPill-label'}>
                {selected && <CheckRoundedIcon aria-hidden className={'TipPill-check'}/>}
                {label}
            </Box>
        </Box>
    );

    if (readOnly) {
        return (
            <Box component={'li'} aria-current={selected || undefined} sx={sx}>
                {content}
            </Box>
        );
    }

    return (
        <ButtonBase
            focusRipple
            disabled={disabled}
            aria-pressed={selected}
            onClick={onClick}
            sx={{
                ...sx,
                width: '100%',
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                '&:hover': {
                    borderColor: selected ? controls.fill : controls.focus,
                    bgcolor: selected ? controls.fill : alpha(controls.focus, 0.04),
                },
                '&:focus-visible': {
                    outline: `2px solid ${controls.focus}`,
                    outlineOffset: 2,
                },
            }}
        >
            {content}
        </ButtonBase>
    );
}

export default TipPill;
